
import React, { useState } from 'react';
import MainLayout from "@/components/MainLayout";
import { Badge } from "@/components/ui/badge";
import { useSupabaseAuth } from '@/hooks/auth';
import UserSearchBar from '@/components/admin/UserSearchBar';
import ApprovalRequestsTab from './ApprovalRequestsTab';
import EnhancedUserTable from '@/components/admin/EnhancedUserTable';
import EnhancedBulkUserActions from '@/components/admin/EnhancedBulkUserActions';
import RealTimeAssignmentTracker from '@/components/admin/RealTimeAssignmentTracker';
import UserManagementDialogs from '@/components/admin/UserManagementDialogs';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserWithRole, UserRole } from './types';
import { useUserManagement } from '@/hooks/useUserManagement';
import { useUserActions } from '@/components/admin/UserManagementActions';
import { OrphanedUserScanner } from '@/components/admin/OrphanedUserScanner';
import { OrphanedUserCleanupHistory } from '@/components/admin/OrphanedUserCleanupHistory';

const UserManagement = () => {
  const { hasAccess } = useSupabaseAuth();
  const [selectedUser, setSelectedUser] = useState<UserWithRole | null>(null);
  const [selectedUserForPasswordReset, setSelectedUserForPasswordReset] = useState<UserWithRole | null>(null);
  const [selectedUserForDelete, setSelectedUserForDelete] = useState<UserWithRole | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [isPasswordResetDialogOpen, setIsPasswordResetDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSubmittingInvite, setIsSubmittingInvite] = useState(false);
  const [isSubmittingPasswordReset, setIsSubmittingPasswordReset] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole>("user");
  const [searchQuery, setSearchQuery] = useState('');

  const {
    users,
    approvalRequests,
    loading,
    selectedUsers,
    setSelectedUsers,
    loadData,
    handleBulkApprove,
    handleBulkRoleChange,
    handleUserInvite,
    handlePasswordReset,
    handleRoleUpdate,
    handleApprovalRequestApprove,
    handleApprovalRequestReject,
    handleUserDelete
  } = useUserManagement();

  const { handleUserEdit, handleUserPasswordReset, handleResendCredentials } = useUserActions();

  const onEditUser = (user: UserWithRole) => {
    handleUserEdit(user, setSelectedUser, setSelectedRole, setIsEditDialogOpen);
  };

  const onResetPassword = (user: UserWithRole) => {
    handleUserPasswordReset(user, setSelectedUserForPasswordReset, setIsPasswordResetDialogOpen);
  };

  const onResendCredentials = async (user: UserWithRole) => {
    await handleResendCredentials(user);
  };

  const onUpdateRole = async () => {
    if (selectedUser) {
      const success = await handleRoleUpdate(selectedUser.id, selectedRole);
      if (success) {
        setIsEditDialogOpen(false);
      }
    }
  };

  const onInviteUser = async (userData: { email: string; name: string; role: UserRole }) => {
    setIsSubmittingInvite(true);
    try {
      return await handleUserInvite(userData);
    } finally {
      setIsSubmittingInvite(false);
    }
  };

  const onPasswordReset = async (userData: { userId: string; newPassword: string }) => {
    setIsSubmittingPasswordReset(true);
    try {
      return await handlePasswordReset(userData);
    } finally {
      setIsSubmittingPasswordReset(false);
    }
  };

  const onDeleteUser = (user: UserWithRole) => {
    setSelectedUserForDelete(user);
    setIsDeleteDialogOpen(true);
  };

  const onConfirmDelete = async (userId: string, force: boolean = false) => {
    setIsDeleting(true);
    try {
      return await handleUserDelete(userId, force);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!hasAccess('admin')) {
    return (
      <MainLayout>
        <div className="container mx-auto py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
            <p className="text-muted-foreground">You don't have admin permissions to access this page.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">User Management</h1>
          <p className="text-muted-foreground">Manage users, roles, and permissions</p>
        </div>
        <UserSearchBar 
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onInviteUser={() => setIsInviteDialogOpen(true)}
        />
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList>
          <TabsTrigger value="users">All Users</TabsTrigger>
          <TabsTrigger value="approvals">
            Approval Requests 
            {approvalRequests.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {approvalRequests.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="bulk">Bulk Actions</TabsTrigger>
          <TabsTrigger value="assignments">Assignment Tracking</TabsTrigger>
          <TabsTrigger value="orphaned">Orphaned Users</TabsTrigger>
          <TabsTrigger value="cleanup-history">Cleanup History</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-6">
          <EnhancedUserTable
            users={users}
            loading={loading}
            selectedUsers={selectedUsers}
            onSelectedUsersChange={setSelectedUsers}
            onEditUser={onEditUser}
            onResetPassword={onResetPassword}
            onResendCredentials={onResendCredentials}
            onDeleteUser={onDeleteUser}
          />
        </TabsContent>

        <TabsContent value="approvals" className="space-y-6">
          <ApprovalRequestsTab
            approvalRequests={approvalRequests}
            onApprove={handleApprovalRequestApprove}
            onReject={handleApprovalRequestReject}
          />
        </TabsContent>

        <TabsContent value="bulk" className="space-y-6">
          <EnhancedBulkUserActions
            users={users}
            selectedUsers={selectedUsers}
            onSelectedUsersChange={setSelectedUsers}
            onBulkApprove={handleBulkApprove}
            onBulkRoleChange={handleBulkRoleChange}
            onRefreshData={loadData}
          />
        </TabsContent>

        <TabsContent value="assignments" className="space-y-6">
          <RealTimeAssignmentTracker />
        </TabsContent>

        <TabsContent value="orphaned" className="space-y-6">
          <OrphanedUserScanner />
        </TabsContent>

        <TabsContent value="cleanup-history" className="space-y-6">
          <OrphanedUserCleanupHistory />
        </TabsContent>
      </Tabs>

      <UserManagementDialogs
        isEditDialogOpen={isEditDialogOpen}
        setIsEditDialogOpen={setIsEditDialogOpen}
        selectedUser={selectedUser}
        selectedRole={selectedRole}
        setSelectedRole={setSelectedRole}
        onUpdateRole={onUpdateRole}
        isSuperAdmin={hasAccess('admin')}
        isInviteDialogOpen={isInviteDialogOpen}
        setIsInviteDialogOpen={setIsInviteDialogOpen}
        onInviteUser={onInviteUser}
        isSubmittingInvite={isSubmittingInvite}
        isPasswordResetDialogOpen={isPasswordResetDialogOpen}
        setIsPasswordResetDialogOpen={setIsPasswordResetDialogOpen}
        selectedUserForPasswordReset={selectedUserForPasswordReset}
        onResetPassword={onPasswordReset}
        isSubmittingPasswordReset={isSubmittingPasswordReset}
        isDeleteDialogOpen={isDeleteDialogOpen}
        setIsDeleteDialogOpen={setIsDeleteDialogOpen}
        selectedUserForDelete={selectedUserForDelete}
        onDeleteUser={onConfirmDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default UserManagement;
